import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { getEnabledCountries } from 'utils/country-helper';
import { getShortLanguageCode } from 'utils/format';

type DirectoryCountrySelectProps = {
    /** ISO alpha-2; empty = all countries. */
    value: string;
    onChange: (country: string) => void;
};

/**
 * DD-3698: the directory's country, from the enabled countries, or all of them (the
 * default). Names come from the
 * browser (Intl.DisplayNames) rather than the lazily loaded country-names namespace, which
 * suspends the tree while it loads (see DD-3654).
 */
const DirectoryCountrySelect: FC<DirectoryCountrySelectProps> = ({ value, onChange }) => {
    const { t: translate } = useTranslation(['client-directory']);
    const options = useMemo(() => {
        const language = getShortLanguageCode(currentUserVar()?.localeFormat?.language) || 'en';
        const names = new Intl.DisplayNames([language, 'en'], { type: 'region' });
        const codes = getEnabledCountries().map((country) => country.alpha2Code);
        // A lab outside the enabled countries still sees its own.
        if (value && !codes.includes(value)) codes.push(value);
        return codes
            .map((code) => ({ code, name: names.of(code) ?? code }))
            .sort((first, second) => first.name.localeCompare(second.name, language));
    }, [value]);

    return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="directory-country" shrink>
                {translate('client-directory:DIRECTORY_COUNTRY')}
            </InputLabel>
            <Select
                labelId="directory-country"
                label={translate('client-directory:DIRECTORY_COUNTRY')}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                displayEmpty
                notched
                data-test="supplier-browser.country"
            >
                <MenuItem value="">{translate('client-directory:ALL_COUNTRIES')}</MenuItem>
                {options.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                        {option.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default DirectoryCountrySelect;
